;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import {
  type FormatDistanceFnOptions,
  type FormatDistanceToken,
  format as formatDateFns,
  formatDistanceToNow,
  type Locale,
  parse,
} from 'date-fns';
import { enGB } from 'date-fns/locale';

/**
 * Formats a numeric amount according to the specified currency and locale.
 * Returns the formatted amount with proper decimal and grouping separators based on the locale.
 *
 * @param amount - The amount to format. Can be a number or a string containing a valid number.
 *                String inputs must contain only digits, an optional decimal point, and an optional minus sign.
 * @param currency - The currency code to use for formatting (default: 'EUR')
 * @param locale - The locale to use for number formatting (default: 'en-US')
 * @param hideZeroDecimals - If true, show no decimals for whole numbers, but keep decimals for non-integers. Default is false.
 *
 * @returns A string containing the formatted amount with locale-specific separators
 *
 * @throws {Error} If the input is not a valid number or properly formatted numeric string
 *
 * @example
 * formatAmount(1000) // returns "1,000.00" for en-US
 * formatAmount(1000, "EUR", "de-DE") // returns "1.000,00"
 * formatAmount("1000.50") // returns "1,000.50" for en-US
 * formatAmount(-1000, "USD") // returns "-1,000.00" for en-US
 */
export const formatAmount = (
  amount: number | string,
  currency = 'EUR',
  locale = 'en-US',
  hideZeroDecimals = false
) => {
  // Validate string input format
  if (typeof amount === 'string') {
    // Check if string contains only valid numeric characters (digits, decimal point, and optional minus sign)
    if (!/^-?\d*\.?\d+$/.test(amount)) {
      throw new Error('Invalid amount: string must contain a valid number');
    }
  }

  // Convert string to number if needed
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  // Validate numeric value
  if (Number.isNaN(numericAmount)) {
    throw new Error('Invalid amount: must be a valid number or numeric string');
  }

  // Determine decimal places
  let minimumFractionDigits = 2;
  let maximumFractionDigits = 2;
  if (hideZeroDecimals && Number.isInteger(numericAmount)) {
    minimumFractionDigits = 0;
    maximumFractionDigits = 0;
  }

  // Get the format parts to identify symbols and separators
  const parts = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
    useGrouping: true,
  }).formatToParts(numericAmount);

  // Extract only the needed parts (digits, decimal, and group separators)
  const withoutSymbol = parts
    .filter((part) => ['integer', 'decimal', 'fraction', 'group', 'minusSign'].includes(part.type))
    .map((part) => part.value)
    .join('');

  return withoutSymbol;
};

/**
 * Formats a numeric amount with locale-specific separators, without currency symbols.
 *
 * @param amount - The amount to format. Can be a number or a string containing a valid number.
 * @param locale - The locale to use for number formatting (default: 'en-US')
 * @param hideZeroDecimals - If true, show no decimals for whole numbers, but keep decimals for non-integers. Default is false.
 *
 * @returns A string containing the formatted amount with locale-specific separators
 *
 * @throws {Error} If the input is not a valid number or properly formatted numeric string
 *
 * @example
 * formatNumber(1000) // returns "1,000.00" for en-US
 * formatNumber(1000, 'de-DE') // returns "1.000,00"
 * formatNumber("1000.50") // returns "1,000.50" for en-US
 * formatNumber(-1000) // returns "-1,000.00" for en-US
 */
export const formatNumber = (
  amount: number | string,
  locale = 'en-US',
  hideZeroDecimals = false
) => {
  // Validate string input format
  if (typeof amount === 'string') {
    if (!/^-?\d*\.?\d+$/.test(amount)) {
      throw new Error('Invalid amount: string must contain a valid number');
    }
  }

  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (Number.isNaN(numericAmount)) {
    throw new Error('Invalid amount: must be a valid number or numeric string');
  }

  let minimumFractionDigits = 2;
  let maximumFractionDigits = 2;
  if (hideZeroDecimals && Number.isInteger(numericAmount)) {
    minimumFractionDigits = 0;
    maximumFractionDigits = 0;
  }

  return new Intl.NumberFormat(locale, {
    style: 'decimal',
    minimumFractionDigits,
    maximumFractionDigits,
    useGrouping: true,
  }).format(numericAmount);
};

/**
 * Formats an FX conversion rate.
 *
 * Rates below 0.01 keep up to 8 decimal places so 0.000653452 does not collapse
 * to 0.00. Larger rates use a 2-decimal floor with 4 significant digits.
 *
 * @example
 * formatConversionRate(0.000653452) // "0.00065345"
 * formatConversionRate(0.0007) // "0.0007"
 * formatConversionRate(0.012542) // "0.01254"
 * formatConversionRate(1.182419698481332) // "1.182"
 * formatConversionRate(18234.5678) // "18,234.57"
 */
export const formatConversionRate = (rate: number | string, locale = 'en-US'): string => {
  const numericRate = typeof rate === 'string' ? parseFloat(rate) : rate;

  if (!Number.isFinite(numericRate)) return '';

  const isTinyRate = Math.abs(numericRate) > 0 && Math.abs(numericRate) < 0.01;
  // `roundingPriority` is ES2023 and is not in this repo's ES2022 DOM lib types.
  const options = (
    isTinyRate
      ? {
          minimumFractionDigits: 2,
          maximumFractionDigits: 8,
          useGrouping: true,
        }
      : {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          maximumSignificantDigits: 4,
          roundingPriority: 'morePrecision',
          useGrouping: true,
        }
  ) as Intl.NumberFormatOptions;

  return new Intl.NumberFormat(locale, options).format(numericRate);
};

/**
 * Formats a JavaScript Date object according to the specified format.
 * Uses date-fns for robust date formatting with comprehensive format support.
 *
 * @param date - The Date object to format
 * @param format - The format string using date-fns format tokens (default: 'dd/MM/yyyy')
 *                Common format tokens:
 *                - dd: day with leading zero (01-31)
 *                - d: day without leading zero (1-31)
 *                - MM: month with leading zero (01-12)
 *                - M: month without leading zero (1-12)
 *                - yyyy: full year (2024)
 *                - yy: short year (24)
 *                - HH: hours with leading zero (00-23)
 *                - H: hours without leading zero (0-23)
 *                - mm: minutes with leading zero (00-59)
 *                - m: minutes without leading zero (0-59)
 *                - ss: seconds with leading zero (00-59)
 *                - s: seconds without leading zero (0-59)
 *
 * @returns A string containing the formatted date
 *
 * @throws {Error} If the input is not a valid Date object
 *
 * @example
 * formatDate(new Date('2024-01-15')) // returns "15/01/2024"
 * formatDate(new Date('2024-01-15'), 'MM/dd/yyyy') // returns "01/15/2024"
 * formatDate(new Date('2024-01-15'), 'yyyy-MM-dd') // returns "2024-01-15"
 * formatDate(new Date('2024-01-15T14:30:00'), 'dd/MM/yyyy HH:mm') // returns "15/01/2024 14:30"
 */
export const formatDate = (date: Date, format = 'dd/MM/yyyy'): string => {
  // Validate input
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    throw new Error('Invalid date: must be a valid Date object');
  }

  return formatDateFns(date, format);
};

/**
 * Parses an ISO date string to a Date object, handling timezone issues for date-only strings
 * Date-only strings (YYYY-MM-DD) are treated as local dates to prevent timezone shifts
 * @param dateString - ISO date string (e.g., "2025-11-10" or "2025-11-10T14:30:00Z")
 * @returns Date object in local timezone
 */
export const parseIsoDateString = (dateString: string): Date => {
  // Check if it's a date-only string (YYYY-MM-DD pattern)
  const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;

  if (dateOnlyPattern.test(dateString)) {
    // Append time to treat as local date instead of UTC
    return new Date(`${dateString}T00:00:00`);
  }

  // Space-delimited "YYYY-MM-DD HH:mm:ss" is not valid ISO 8601, so `new Date` falls back to
  // implementation-defined parsing that can swap month/day on ambiguous dates. Parse it
  // deterministically with date-fns instead.
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString)) {
    const parsed = parseDateString(dateString, 'yyyy-MM-dd HH:mm:ss');
    if (parsed) return parsed;
  }

  // For full ISO strings with time, parse normally
  return new Date(dateString);
};

/**
 * Parses a payroll records-date string in MM/YYYY format into its month and year parts
 * @param recordsDateMmYyyy - records date string (e.g., "07/2026")
 * @returns Object with numeric month (1-12) and year, or null if the string is not a valid MM/YYYY date
 */
export const parseRecordsDateMmYyyy = (
  recordsDateMmYyyy: string
): { month: number; year: number } | null => {
  const parts = recordsDateMmYyyy.split('/');
  if (parts.length !== 2) return null;
  const month = Number(parts[0]);
  const year = Number(parts[1]);
  if (!Number.isFinite(month) || !Number.isFinite(year) || month < 1 || month > 12) return null;
  return { month, year };
};

/**
 * Formats an ISO date string (YYYY-MM-DD or ISO 8601), handling timezone issues and validation
 * This function treats date-only strings (YYYY-MM-DD) as local dates to prevent timezone shifts
 * @param dateString - ISO date string (e.g., "2025-11-10" or "2025-11-10T14:30:00Z")
 * @param formatPattern - date-fns format pattern (default: 'dd/MM/yyyy')
 * @returns Formatted date string, or original string if parsing fails
 */
export const formatIsoDateString = (dateString: string, formatPattern = 'dd/MM/yyyy'): string => {
  try {
    const date = parseIsoDateString(dateString);

    // Validate the parsed date
    if (Number.isNaN(date.getTime())) {
      return dateString;
    }

    return formatDateFns(date, formatPattern);
  } catch {
    return dateString;
  }
};

/**
 * Parses a date string in DD/MM/YYYY HH:mm:ss format and formats it to a custom format.
 * This function is specifically designed to handle API date strings in the DD/MM/YYYY HH:mm:ss format.
 *
 * @param dateString - The date string in DD/MM/YYYY HH:mm:ss format (e.g., "29/07/2025 00:00:00")
 * @param outputFormat - The desired output format using date-fns format tokens (default: 'dd/MM/yyyy')
 *                Common format tokens:
 *                - dd: day with leading zero (01-31)
 *                - d: day without leading zero (1-31)
 *                - MM: month with leading zero (01-12)
 *                - M: month without leading zero (1-12)
 *                - yyyy: full year (2024)
 *                - yy: short year (24)
 *                - HH: hours with leading zero (00-23)
 *                - H: hours without leading zero (0-23)
 *                - mm: minutes with leading zero (00-59)
 *                - m: minutes without leading zero (0-59)
 *                - ss: seconds with leading zero (00-59)
 *                - s: seconds without leading zero (0-59)
 *
 * @returns A string containing the formatted date
 *
 * @throws {Error} If the input is not a valid date string in the expected format
 *
 * @example
 * formatDateString("29/07/2025 00:00:00") // returns "29/07/2025"
 * formatDateString("15/07/2025 14:30:00", "dd/MM/yyyy HH:mm") // returns "15/07/2025 14:30"
 * formatDateString("10/07/2025 09:15:30", "yyyy-MM-dd") // returns "2025-07-10"
 * formatDateString("29/07/2025 00:00:00", "MMMM dd, yyyy") // returns "July 29, 2025"
 */
export const formatDateString = (dateString: string, outputFormat = 'dd/MM/yyyy'): string => {
  // Validate input
  if (typeof dateString !== 'string') {
    console.error('Invalid date string: must be a string');
    return '';
  }

  try {
    // Parse the date string using date-fns parse function
    // The format 'dd/MM/yyyy HH:mm:ss' tells parse how to interpret the input string
    const parsedDate = parse(dateString, 'dd/MM/yyyy HH:mm:ss', new Date());

    // Validate the parsed date
    if (Number.isNaN(parsedDate.getTime())) {
      console.error(`Invalid date values in string: ${dateString}`);
      return '';
    }

    // Format the parsed date using the desired output format
    return formatDateFns(parsedDate, outputFormat);
  } catch (error) {
    console.error(
      `Failed to parse date string "${dateString}": ${error instanceof Error ? error.message : 'Unknown error'}`
    );
    return '';
  }
};

/**
 * Parses a date string in DD/MM/YYYY HH:mm:ss format and formats it to a custom format.
 * This function is designed to handle date strings in any format.
 *
 * @param dateString - The date string in DD/MM/YYYY HH:mm:ss format (e.g., "29/07/2025 00:00:00")
 * @param outputFormat - The desired output format using date-fns format tokens (default: 'dd/MM/yyyy')
 *                Common format tokens:
 *                - dd: day with leading zero (01-31)
 *                - d: day without leading zero (1-31)
 *                - MM: month with leading zero (01-12)
 *                - M: month without leading zero (1-12)
 *                - yyyy: full year (2024)
 *                - yy: short year (24)
 *                - HH: hours with leading zero (00-23)
 *                - H: hours without leading zero (0-23)
 *                - mm: minutes with leading zero (00-59)
 *                - m: minutes without leading zero (0-59)
 *                - ss: seconds with leading zero (00-59)
 *                - s: seconds without leading zero (0-59)
 *
 * @returns A Date object
 *
 * @throws {Error} If the input is not a valid date string in the expected format
 *
 * @example
 * parseDateString("29/07/2025") // returns a Date object
 * parseDateString("29/07/2025 00:00:00") // returns a Date object
 * parseDateString("15/07/2025 14:30:00", "dd/MM/yyyy HH:mm") // returns a Date object
 * parseDateString("2025-07-10", "yyyy-MM-dd") // returns a Date object
 * parseDateString("July 29, 2025", "MMMM dd, yyyy") // returns a Date object
 */
export const parseDateString = (
  dateString: string | null | undefined,
  format = 'dd/MM/yyyy'
): Date | undefined => {
  if (dateString == null || dateString.trim() === '') {
    return undefined;
  }
  const date = parse(dateString, format, new Date());
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  return date;
};

/**
 * Formats an ISO date string (YYYY-MM-DD or full ISO 8601) to a custom format.
 * This function safely handles ISO standard dates from APIs and displays them in user-friendly formats.
 *
 * @param isoDateString - The ISO date string (e.g., "2025-10-14", "2025-10-15T08:58:29.000000+00:00")
 * @param outputFormat - The desired output format using date-fns format tokens (default: 'dd/MM/yyyy')
 * @returns A string containing the formatted date, or an empty string if invalid
 *
 * @example
 * formatISODate("2025-10-14") // returns "14/10/2025"
 * formatISODate("2025-10-15T08:58:29.000000+00:00") // returns "15/10/2025"
 * formatISODate("2025-10-14", "MMM dd, yyyy") // returns "Oct 14, 2025"
 * formatISODate("invalid") // returns ""
 */
export const formatISODate = (isoDateString: string, outputFormat = 'dd/MM/yyyy'): string => {
  // Validate input
  if (typeof isoDateString !== 'string') {
    console.error('Invalid date string: must be a string');
    return '';
  }

  try {
    // JavaScript's Date constructor natively handles ISO 8601 formats
    const date = new Date(isoDateString);

    // Check if it's a valid date
    if (Number.isNaN(date.getTime())) {
      console.error(`Invalid date values in string: ${isoDateString}`);
      return '';
    }

    return formatDateFns(date, outputFormat);
  } catch (error) {
    console.error(
      `Failed to parse date string "${isoDateString}": ${error instanceof Error ? error.message : 'Unknown error'}`
    );
    return '';
  }
};

/**
 * Formats a large number to a compact notation.
 * @param number - The number to format.
 * @param startFrom - The number to start from which to use compact notation.
 * @returns A string containing the formatted number.
 *
 * @example
 * formatLargeNumber(1000) // returns "1,000"
 * formatLargeNumber(100000) // returns "100k"
 * formatLargeNumber(1000000) // returns "1M"
 * formatLargeNumber(1000000000) // returns "1B"
 */
export const formatLargeNumber = (number: number, startFrom = 10000): string => {
  if (number < startFrom) {
    return number.toLocaleString();
  }
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(number);
};

/**
 * Converts a size value between units.
 *
 * @param value - The numeric value to convert
 * @param inputUnit - The unit of the input value (default: 'kb')
 * @param outputUnit - The desired output unit (default: 'mb')
 * @param decimals - Number of decimal places to keep (default: 2)
 * @returns A string in the form "<value> <UNIT>" (e.g., "1.50 MB")
 *
 * Examples:
 * convertBytes(2048) // "2.00 MB" (defaults kb -> mb)
 * convertBytes(1048576, 'b', 'mb') // "1.00 MB"
 * convertBytes(1.5, 'gb', 'mb') // "1536.00 MB"
 */
export const convertBytes = (
  value: number,
  inputUnit: 'b' | 'kb' | 'mb' | 'gb' | 'tb' = 'kb',
  outputUnit: 'b' | 'kb' | 'mb' | 'gb' | 'tb' = 'mb',
  decimals = 2
): string => {
  const unitToPower: Record<'b' | 'kb' | 'mb' | 'gb' | 'tb', number> = {
    b: 0,
    kb: 1,
    mb: 2,
    gb: 3,
    tb: 4,
  };

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error('Value must be a finite number');
  }

  const inputPower = unitToPower[inputUnit];
  const outputPower = unitToPower[outputUnit];

  // Normalize to bytes, then to desired unit
  const bytes = value * 1024 ** inputPower;
  const converted = bytes / 1024 ** outputPower;

  const fixed =
    Number.isFinite(decimals) && decimals >= 0 ? converted.toFixed(decimals) : String(converted);
  return `${fixed} ${outputUnit.toUpperCase()}`;
};

// Remap about tokens to exact tokens
// aboutXDays does not exist, there is only precise days
const ABOUT_TOKEN_MAP: Record<string, FormatDistanceToken> = {
  aboutXHours: 'xHours',
  aboutXWeeks: 'xWeeks',
  aboutXMonths: 'xMonths',
  aboutXYears: 'xYears',
};

const createCustomLocale = (locale: Locale) => ({
  ...locale,
  formatDistance: (
    token: FormatDistanceToken,
    count: number,
    options?: FormatDistanceFnOptions
  ) => {
    // Custom logic to show weeks when days >= 7
    if (token === 'xDays' && count >= 7) {
      const weeks = Math.floor(count / 7);
      return locale.formatDistance('xWeeks', weeks, options);
    }

    // Remove "about" prefix
    if (token in ABOUT_TOKEN_MAP) {
      const exactToken = ABOUT_TOKEN_MAP[token]!;
      return locale.formatDistance(exactToken, count, options);
    }

    return locale.formatDistance(token, count, options);
  },
});

interface FormatRelativeDateProps {
  dateString: string;
  format?: string;
  addSuffix?: boolean;
  locale?: Locale;
}

/**
 * Parses a date string with the given format and returns the relative time from now
 * (e.g., "2 days ago", "1 month ago").
 * Uses a custom locale that removes "about" prefixes and converts days >= 7 to weeks.
 *
 * @param dateString - The date string to parse
 * @param format - The date-fns format of the input string (default: 'yyyy-MM-dd HH:mm:ss')
 * @param addSuffix - Whether to add "ago" or "in" suffix (default: true)
 *
 * @returns Relative time string, or empty string if parsing fails
 *
 * @example
 * formatRelativeDate({ dateString: "2025-01-08 14:22:00" })                                // "1 year ago"
 * formatRelativeDate({ dateString: "27/01/2026 00:00:00", format: "dd/MM/yyyy HH:mm:ss" }) // "1 month ago"
 */
export const formatRelativeDate = ({
  dateString,
  format = 'yyyy-MM-dd HH:mm:ss',
  addSuffix = true,
  locale = enGB,
}: FormatRelativeDateProps): string => {
  const date = parseDateString(dateString, format);

  if (!date) {
    console.error(`Failed to parse date string "${dateString}" with format "${format}"`);
    return '';
  }

  const customLocale = createCustomLocale(locale);
  return formatDistanceToNow(date, { locale: customLocale, addSuffix });
};

/**
 * Extracts initials from a full name string.
 * Returns the first letter of each word, up to a maximum of 2 initials.
 *
 * @param fullName - The full name to extract initials from
 * @param maxInitials - Maximum number of initials to return (default: 2)
 *
 * @returns A string containing the initials in uppercase
 *
 * @example
 * getInitials("John Doe") // returns "JD"
 * getInitials("Maria Santos Silva") // returns "MS" (first 2 initials only)
 * getInitials("Alice") // returns "A"
 * getInitials("") // returns ""
 * getInitials("John Doe Smith", 3) // returns "JDS"
 */
export const getInitials = (fullName: string, maxInitials = 2): string => {
  if (!fullName) return '';

  return fullName
    .trim()
    .split(' ')
    .filter((name) => name.length > 0)
    .map((name) => name.charAt(0).toUpperCase())
    .slice(0, maxInitials)
    .join('');
};
